import {
  recordSwallowedLifecycleRejection,
  tagLifecycleRejection,
} from './lifecycleRejectionDiagnostic';

type QueryLifecycleRejection = {
  error: unknown;
  isUnhandledError: unknown;
  meta: unknown;
};

type LifecycleHandler = (arg: unknown, api: LifecycleApi) => unknown;

type LifecycleApi = {
  queryFulfilled?: Promise<unknown>;
  [key: string]: unknown;
};

type EndpointDefinition = {
  onQueryStarted?: LifecycleHandler;
};

const QUERY_LIFECYCLE_REJECTION_KEYS = [
  'error',
  'isUnhandledError',
  'meta',
] as const;

const GUARD_INSTALLED = '__assemblyQueryLifecycleRejectionGuard';

// RTK rejects `queryFulfilled` with this plain object. PostHog turns an
// unhandled rejection of it into a stackless "Object captured as exception".
// A promise that already has a rejection handler is not reported; the listener
// below only stops events the browser still dispatches.
export const isQueryLifecycleRejection = (
  reason: unknown,
): reason is QueryLifecycleRejection => {
  if (
    typeof reason !== 'object' ||
    reason === null ||
    reason instanceof Error
  ) {
    return false;
  }

  const record = reason as Record<string, unknown>;
  return QUERY_LIFECYCLE_REJECTION_KEYS.every((key) => key in record);
};

const isHandledLifecycleReason = (reason: unknown): boolean =>
  isQueryLifecycleRejection(reason) ||
  (reason instanceof Error &&
    'error' in reason &&
    'isUnhandledError' in reason &&
    'meta' in reason);

const guardOnQueryStarted =
  (handler: LifecycleHandler, endpoint: string): LifecycleHandler =>
  (arg, api) => {
    // Do not wrap queryFulfilled itself. A derived `.then` settles a tick
    // later than the original, so boot handlers lose the race with the
    // request they were waiting on.
    // A side catch, not a replacement: it only tags the rejection with this
    // endpoint so the window listener can name it later.
    api.queryFulfilled?.catch((reason: unknown) =>
      tagLifecycleRejection(reason, endpoint),
    );
    const result = handler(arg, api);

    // RTK ignores this promise. If the handler does not catch, it rejects with
    // the lifecycle object and the browser reports that rejection.
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      return (result as Promise<unknown>).catch((reason: unknown) => {
        if (isHandledLifecycleReason(reason)) {
          recordSwallowedLifecycleRejection('handler_catch', reason, endpoint);
          return undefined;
        }
        throw reason;
      });
    }

    return result;
  };

// Unconstrained on purpose: constraining to an endpoint type makes createApi
// infer an empty endpoint map.
export const guardEndpointMap = <T extends Record<string, any>>(
  definitions: T,
): T => {
  for (const [endpoint, definition] of Object.entries(definitions)) {
    if (typeof definition?.onQueryStarted !== 'function') {
      continue;
    }
    const guarded = definition as EndpointDefinition;
    guarded.onQueryStarted = guardOnQueryStarted(
      definition.onQueryStarted,
      endpoint,
    );
  }
  return definitions;
};

type ApiWithInject = {
  // The real signature is createApi's. `any` keeps that full API type intact;
  // a narrower function type collapses every endpoint to `unknown`.
  injectEndpoints: (...args: any[]) => any;
};

export const guardInjectedLifecycles = <Api extends ApiWithInject>(
  api: Api,
): Api => {
  const originalInject = api.injectEndpoints.bind(api);
  api.injectEndpoints = ((inject: {
    endpoints: (build: unknown) => Record<string, EndpointDefinition>;
    overrideExisting?: boolean | 'throw';
  }) => {
    const injected = originalInject({
      ...inject,
      endpoints: (build: unknown) => guardEndpointMap(inject.endpoints(build)),
    });
    // originalInject is any so createApi's generic signature stays intact.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return injected;
  }) as Api['injectEndpoints'];
  return api;
};

export const installUnhandledQueryLifecycleRejectionGuard = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const guardedWindow = window as unknown as Window & Record<string, unknown>;
  if (guardedWindow[GUARD_INSTALLED]) {
    return;
  }
  guardedWindow[GUARD_INSTALLED] = true;

  // Capture phase runs before Sentry and PostHog, which record this rejection
  // even though RTK already attached a catch to `queryFulfilled`.
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      if (!isQueryLifecycleRejection(event.reason)) {
        return;
      }
      recordSwallowedLifecycleRejection('window_listener', event.reason);
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true,
  );
};

// Installed on import so it runs before Sentry.init and PostHog's exception
// autocapture register their own unhandledrejection listeners.
installUnhandledQueryLifecycleRejectionGuard();
